/**
 * OrderUpgradePage — public "complete your experience" page linked from the
 * missed-upgrades email. Lets a past buyer add the upgrades (add-ons) they
 * skipped, attached to their original order (no re-buying tickets).
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Gift, Minus, Plus, Loader2, CheckCircle2, Calendar, MapPin, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderUpgradePage() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [qty, setQty] = useState({});
  const [buying, setBuying] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/orders/${orderId}/upgrade-options`)
      .then(res => {
        setData(res.data);
        const init = {};
        (res.data.addons || []).forEach(a => { init[a.ticket_type_id] = a.suggested_qty || 1; });
        setQty(init);
      })
      .catch(err => setError(err.response?.data?.detail || 'Could not load this order'));
  }, [orderId]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <p className="text-muted-foreground" data-testid="upgrade-error">{error}</p>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
    </div>
  );

  const subtotal = (data.addons || []).reduce((s, a) => s + a.price * (qty[a.ticket_type_id] || 0), 0);
  const units = Object.values(qty).reduce((s, v) => s + v, 0);
  const fee = (() => {
    const fc = data.fee_config || {};
    if (!fc.buyer_pays || subtotal === 0 || units === 0) return 0;
    const sc = Math.round(subtotal * 100);
    return (Math.round(sc * (fc.percent || 0) / 100) + (fc.flat_cents || 0) * units
      + Math.round(sc * (fc.payment_processing_percent || 0) / 100)) / 100;
  })();
  const total = subtotal + fee;

  const setAddonQty = (a, delta) => {
    setQty(prev => {
      const next = Math.max(0, Math.min(a.remaining, (prev[a.ticket_type_id] || 0) + delta));
      return { ...prev, [a.ticket_type_id]: next };
    });
  };

  const handleUpgrade = async () => {
    const items = Object.entries(qty).filter(([_, q]) => q > 0)
      .map(([ttId, q]) => ({ ticket_type_id: ttId, quantity: q }));
    if (items.length === 0) { toast.error('Pick at least one upgrade'); return; }
    setBuying(true);
    try {
      const res = await axios.post(`${API}/events/${data.event.event_id}/purchase`, {
        items, upgrade_order_id: orderId,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setDone(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not complete the upgrade');
    } finally {
      setBuying(false);
    }
  };

  const ev = data.event;
  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="order-upgrade-page">
      <div className="max-w-xl mx-auto space-y-5">
        <div className="text-center space-y-1">
          <Badge className="bg-indigo-500/15 text-indigo-300 border-indigo-500/30 gap-1.5"><Sparkles className="h-3 w-3" /> Complete your experience</Badge>
          <h1 className="text-2xl font-bold" data-testid="upgrade-heading">Hi {data.buyer_first_name}, finish your upgrades</h1>
          <p className="text-sm text-muted-foreground">
            You have {data.tickets} ticket{data.tickets !== 1 ? 's' : ''} and {data.upgrades} upgrade{data.upgrades !== 1 ? 's' : ''} for this event.
          </p>
        </div>

        <Card className="overflow-hidden border-border">
          {ev.image_url && <img src={ev.image_url} alt={ev.name} className="w-full h-36 object-cover" />}
          <CardContent className="p-4 space-y-1">
            <h2 className="font-bold" data-testid="upgrade-event-name">{ev.name}</h2>
            {ev.date && <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {ev.date}{ev.time ? ` · ${ev.time}` : ''}</p>}
            {ev.venue_name && <p className="text-xs text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {ev.venue_name}</p>}
          </CardContent>
        </Card>

        {data.upgraded_orders > 0 && data.upgrade_pct > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-300" data-testid="upgrade-social-proof">
            <strong>{data.upgrade_pct}%</strong> of buyers for this event have already upgraded.
          </div>
        )}

        {done ? (
          <Card className="border-emerald-500/40">
            <CardContent className="p-6 text-center space-y-2">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
              <h2 className="font-bold text-lg" data-testid="upgrade-success">Upgrades added!</h2>
              <p className="text-sm text-muted-foreground">A confirmation was sent to {data.buyer_email_masked}. They're attached to your existing tickets — see you at the event.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-2.5">
              {(data.addons || []).map(a => {
                const q = qty[a.ticket_type_id] || 0;
                return (
                  <div key={a.ticket_type_id} className={`p-3.5 rounded-lg border border-dashed ${q > 0 ? 'border-fuchsia-500/70 bg-fuchsia-500/10' : 'border-fuchsia-500/30'} transition-all`} data-testid={`upgrade-addon-${a.ticket_type_id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex items-start gap-2.5">
                        <Gift className="h-4 w-4 mt-0.5 flex-shrink-0 text-fuchsia-400" />
                        <div className="min-w-0">
                          <p className="font-semibold text-sm">{a.name}</p>
                          {a.description && <p className="text-xs text-muted-foreground line-clamp-2">{a.description}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <p className="font-bold text-fuchsia-400">{a.price === 0 ? 'Free' : `$${a.price}`}</p>
                        <div className="flex items-center gap-2">
                          <Button size="icon" variant="outline" className="h-7 w-7 rounded-full border-fuchsia-500/40" onClick={() => setAddonQty(a, -1)} data-testid={`upgrade-minus-${a.ticket_type_id}`}><Minus className="h-3 w-3" /></Button>
                          <span className="w-6 text-center font-semibold text-sm" data-testid={`upgrade-qty-${a.ticket_type_id}`}>{q}</span>
                          <Button size="icon" variant="outline" className="h-7 w-7 rounded-full border-fuchsia-500/40" onClick={() => setAddonQty(a, 1)} data-testid={`upgrade-plus-${a.ticket_type_id}`}><Plus className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <Card className="border-border">
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Upgrades ({units})</span><span>{subtotal === 0 ? 'Free' : `$${subtotal.toFixed(2)}`}</span></div>
                {fee > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Processing fee</span><span>${fee.toFixed(2)}</span></div>}
                <div className="flex justify-between font-bold border-t border-border pt-2"><span>Total</span><span data-testid="upgrade-total">{total === 0 ? 'Free' : `$${total.toFixed(2)}`}</span></div>
                <Button className="w-full rounded-full font-bold" disabled={buying || units === 0} onClick={handleUpgrade} data-testid="upgrade-submit-btn">
                  {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : total === 0 ? 'Add my upgrades' : `Add upgrades — $${total.toFixed(2)}`}
                </Button>
                <p className="text-[11px] text-center text-muted-foreground">Attached to your order for {data.buyer_email_masked} — your tickets stay exactly as they are.</p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
